// ListProperties Types ===================================

import { MatterEventTypes, MatterStatus } from "@/utils/enums.ts";

interface ListProperties {
  ID: number;
  Created?: string;
  Author?: any;
  Modified?: string;
  Editor?: IUser;
}

// ISharepointListProperty Types ===================================

export interface ISharepointListProperty {
  name: string;
  isLookup: boolean;
  lookupListProperty: string[];
}

// IUser Types ===================================

export interface IUser {
  Title: string;
  Id: number;
  FirstName?: string;
  LastName?: string;
  EMail?: string;
  Email?: string;
  MobilePhone?: string;
  WorkPhone?: string;
  Department?: string;
  JobTitle?: string;
  Name?: string;
  ContentTypeId?: string;
  IsSiteAdmin?: boolean;
  isAdminMember?: boolean;
}

// IKeyword Types ===================================

export interface IKeywordGeneric extends ListProperties {
  keyword: string;
}

export type IKeyword = IKeywordGeneric & {
  section: ISection;

  vsdlId: never;
  sectionTitle: never;
};

export type IKeywordGuest = IKeywordGeneric & {
  section: never;
  active: boolean;

  vsdlId: number;
  sectionTitle: string;
};

export type IKeywordAll = IKeyword | IKeywordGuest;

// IClientType Types ===================================

export interface IClientTypeGeneric extends ListProperties {
  clientTypeTitle: string;
}

export type IClientType = IClientTypeGeneric & {
  vsdlId: never;
};

export type IClientTypeGuest = IClientTypeGeneric & {
  vsdlId: number;
};

export type IClientTypeAll = IClientType | IClientTypeGuest;

// IQuestionType Types ===================================

export interface IQuestionTypeGeneric extends ListProperties {
  questionTitle: string;
  questionDescription?: string;
  questionTemplate?: string;
}

export type IQuestionType = IQuestionTypeGeneric & {
  vsdlId: never;
};

export type IQuestionTypeGuest = IQuestionTypeGeneric & {
  vsdlId: number;
};

export type IQuestionTypeAll = IQuestionType | IQuestionTypeGuest;

// ISection Types ===================================

export interface ISectionGeneric extends ListProperties {
  active: boolean;
  sectionTitle: string;
  sectionType: string;
}

export type ISection = ISectionGeneric & {
  connectedSections?: ISection[];

  connectedSectionsTitles: never;
  vsdlId: never;
};

export type ISectionGuest = ISectionGeneric & {
  connectedSections: never;

  connectedSectionsTitles: string;
  vsdlId: number;
};

export type ISectionAll = ISection | ISectionGuest;

// ICombinations Types ===================================

export interface ICombinationGeneric extends ListProperties {}

export type ICombination = ICombinationGeneric & {
  section: ISection;
  clientType: IClientType;
  questionType: IQuestionType;

  sectionTitle: never;
  questionTypeTitle: never;
  clientTypeTitle: never;
  vsdlId: never;
};

export type ICombinationGuest = ICombinationGeneric & {
  section: never;
  clientType: never;
  questionType: never;

  sectionTitle: string;
  questionTypeTitle: string;
  clientTypeTitle: string;
  vsdlId: number;
};

export type ICombinationAll = ICombination | ICombinationGuest;

// IFAQ Types ===================================
// TODO - all of these are not used, decide on what information to show
export interface IFAQGeneric extends ListProperties {
  question: string;
  answer: string;
  active: boolean;
  FAQTitle: string;
  approvedDate?: string;
  reference?: string;
  public: boolean;
  proposalForId?: number;
  approvedById?: number;
  documents?: any;
  verifiedDate?: string;
  isDraft: boolean;
  roleTitle: string;
  sectionId?: number;
  questionTypeId?: number;
  clientTypesId?: any;
}

export type IFAQ = IFAQGeneric & {
  section: ISection;
  questionType: IQuestionType;
  clientTypes: IClientType[];

  sectionTitle: never;
  questionTypeTitle: never;
  clientTypesTitle: never;
  vsdlId: never;
};

export type IFAQGuest = IFAQGeneric & {
  section: never;
  questionType: never;
  clientTypes: never;

  sectionTitle: string;
  questionTypeTitle: string;
  clientTypesTitle: string;
  vsdlId: number;
};

export type IFAQAll = IFAQ | IFAQGuest;

// FAQ File Types ===================================

export interface IFAQFileGeneric extends ListProperties {
  Title: string;
}

export type IFAQFile = IFAQFileGeneric & {
  belongsToFAQ: IFAQ;
  vsdlId: never;
};

export type IFAQFileGuest = IFAQFileGeneric & {
  belongsToFAQ: number;
  vsdlId: number;
};

export type IFAQFileAll = IFAQFile | IFAQFileGuest;

// IEmail Types ===================================

export interface IEmail {
  to: string[];
  subject: string;
  body: string;
}

export type valueLabelPair<T> = { value: T; label: string };

// Objects =========================================

export const UserObject: IUser = {
  Id: -1,
  Title: "",
  FirstName: "",
  LastName: "",
  EMail: "",
  MobilePhone: "",
  Department: "",
  JobTitle: "",
  IsSiteAdmin: false
};

// Keyword with section object is users's logged in
// if logistikportalen (open for guests) it has the sectionTitle property
export interface IKeywordValueLabel {
  value: number;
  label: string;
  sectionTitle?: string;
  section?: ISection;
}

export interface IImportDate extends ListProperties {
  date: string;
}

export type ImportFileType = {
  sections: ISection[];
  clientTypes: IClientType[];
  combinations: ICombination[];
  questionTypes: IQuestionType[];
  keywords: IKeyword[];
  FAQs: IFAQ[];
  FAQFiles: IFAQFile[];
};

export interface IRolePosition {
  positions: string;
}

export interface IRole extends ListProperties {
  active: boolean;
  section: ISection;
  members: IUser[];
  roleType: string;
  roleTitle: string;
  roleDescription: string;
  testMember?: string;
  parked: boolean;
  parkedUntil?: string;
  parkReason?: string;
  traditionBearer?: IRole;
  matterNotifications: boolean;
  faqNotifications: boolean;
  roleLevel: number;
  notificationRedundancy: boolean;
  reminders: boolean;
  autoEscalateMatters: boolean;
  supervisingRole?: IRole;
  subordinateRoles: IRole[];
  rolePosition: IRolePosition;
}

export interface IMatter extends ListProperties {
  matterId: string;
  matterTitle: string;
  matterDescription: string;
  clientName?: string;
  clientPhone?: string;
  clientMisc?: string;
  clientMail?: string;
  csCommentDraft?: string;
  matterResponse?: string;
  remindedUsers?: number[];
  recievedVia?: string;
  checkedOutByUser?: IUser;
  answeredByUser?: IUser;
  lastReminderSent?: boolean;
  escalated?: boolean;
  testMatter?: boolean;
  prioritisedMatter?: boolean;
  cc?: string;
  bcc?: string;
  checkedOutAndReminded?: boolean;
  includeSpecialistName?: boolean;
  journalizeMatter?: boolean;
  clientMailArrivalDate?: string;
  answerDeadline?: string;
  questionType: IQuestionType;
  clientType: IClientType;
  answeredWithFaq?: number;
  closedDate?: string | null;
  modifiedDate: string;
  createdDate?: string;
  currentlyAssignedToRole?: IRole;
  answeredByRole?: IRole;
  createdByRole?: IRole;
  escalatedByRole?: IRole;
  status?: MatterStatus;
  section: ISection;
  isInternal?: boolean;
  sectionId?: number;
  questionTypeId?: number;
  clientTypeId?: number;
  currentlyAssignedToRoleId?: number;
  checkedOutByUserId?: number | null;
  internalDueDate?: string;
  assignedByRole?: IRole;
  answeredWithFaqId?: number;
  answeredByUserId?: number;
  answeredByRoleId?: number;
  assignedByRoleId?: number;
  imported?: boolean;
  customerServiceUser?: number;
  processStep?: number;
  deviationLevel?: number;
  handlingTime?: string;
  reopened?: string;
}
export interface IMatterEvent extends ListProperties {
  commentAuthorId?: number;
  authorRoleId?: number;
  commentAuthor: IUser;
  authorRole: IRole;
  eventType: MatterEventTypes;
  belongsToMatter?: { ID: number };
  comment: string;
  recievingRole: IRole;
  recievingRoleId?: number;
  belongsToMatterId?: number;
  replyDeadline?: string;
}
